import React, { useState, useEffect, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import BN from 'bignumber.js';
import { useTranslation } from 'react-i18next';

// i18n
import './i18n';
import i18n from 'i18next';

// util
import {
  middleEllipsis
} from './utils/address';

// plugin
import Wallet from './plugin/Wallet';
import { ADD_GBIFI_TOKEN, ADD_BIFI_TOKEN } from './plugin/config/address';
import {
  SIG_BALANCE,
  SIG_ALLOWANCE,
  SIG_APPROVE,
  SIG_DEPOSIT_FOR,
  SIG_WITHDRAW_TO
} from './plugin/config/signature';
import { MAX_VALUE_UINT256 } from './plugin/config/maxValue';
import { BLOCK_TERM, DEFAULT_DECIMALS } from './plugin/config/expectedBlock';
import tokens from './plugin/config/tokens';

// asset
import './App.css';
import './styles/materialButton.css';
import PopupRegisterToken from './components/popup-register-token';
import Loading from './components/loading';
import logo from './assets/logo.svg';
import main from './assets/main.svg';
import ArrowDownward from '@material-ui/icons/ArrowDownward';

// style
const enabledStyleProvider = (enabled: boolean): React.CSSProperties => {
  if (enabled) {
    return {};
  } else {
    return {
      pointerEvents: 'none',
      opacity: '0.4'
    };
  }
};

// app
function App() {
  const { t } = useTranslation();

  // state
  const [isInitialized, setInitialized] = useState(false);
  const [inputAmount, setInputAmount] = useState('');
  const [bifiBalance, setBifiBalance] = useState('0');
  const [gbifiBalance, setGbifiBalance] = useState('0');
  const [selectedTokenID, setSelectedTokenID] = useState('bifi');
  const [walletAddress, setWalletAddress] = useState('');
  const [walletAllowance, setWalletAllowance] = useState('0');
  const [chainID, setChainID] = useState('');
  const [wallet] = useState(new Wallet(setWalletAddress, setChainID));
  const [popup, setPopup] = useState(false);

  // memo
  const selectedToken = useMemo(
    () => tokens.find(token => token.id === selectedTokenID),
    [selectedTokenID]
  );
  const selectedTokenImage = useMemo(
    () => (selectedToken || {}).image,
    [selectedToken]
  );
  const selectedTokenSymbol = useMemo(
    () => (selectedToken || {}).symbol || '',
    [selectedToken]
  );
  const selectedTokenBalance = useMemo(
    () => selectedTokenID === 'bifi' ? bifiBalance : gbifiBalance,
    [selectedTokenID, bifiBalance, gbifiBalance]
  );
  const unselectedToken = useMemo(
    () => tokens.find(token => token.id !== selectedTokenID),
    [selectedTokenID]
  );
  const unselectedTokenImage = useMemo(
    () => (unselectedToken || {}).image,
    [unselectedToken]
  );
  const unselectedTokenSymbol = useMemo(
    () => (unselectedToken || {}).symbol || '',
    [unselectedToken]
  );
  const inputAmountEnabled = useMemo(
    () => isInitialized && new BN(selectedTokenBalance || '0').gt('0'),
    [isInitialized, selectedTokenBalance]
  );
  const buttonApproveTokenRequired = useMemo(
    () =>
      isInitialized && Wallet.isAddress(walletAddress) && new BN(walletAllowance || '0').lte('0'),
    [isInitialized, walletAddress, walletAllowance]
  );
  const buttonSwapEnabled = useMemo(
    () =>
      isInitialized &&
      new BN(walletAllowance || '0').gt('0') &&
      new BN(inputAmount || '0').gt('0'),
    [isInitialized, walletAllowance, inputAmount]
  );
  const errorOccured = useMemo(
    () =>
      isInitialized &&
      new BN(inputAmount || '0').gt('0') &&
      new BN(walletAllowance || '0').lte('0'),
    [isInitialized, walletAllowance, inputAmount]
  );
  const initializing = useMemo(
    () => walletAddress && chainID && !isInitialized,
    [walletAddress, chainID, isInitialized]
  );

  // effect
  useEffect(() => {
    const syncData = (): void => {
      if (!(walletAddress && chainID)) {
        wallet
          .connectWallet()
          .catch(error => console.error('connect wallet', error));
        setInitialized(false);
        return;
      }

      // token call
      wallet
        .contractCall(ADD_GBIFI_TOKEN, SIG_BALANCE, [walletAddress], ['uint256'])
        .then(response => setGbifiBalance(Wallet.bn2Str(new BN(response).div(DEFAULT_DECIMALS))))
        .catch(() => setGbifiBalance('0'));
      wallet
        .contractCall(ADD_BIFI_TOKEN, SIG_BALANCE, [walletAddress], ['uint256'])
        .then(response => setBifiBalance(Wallet.bn2Str(new BN(response).div(DEFAULT_DECIMALS))))
        .catch(() => setBifiBalance('0'));
      wallet
        .contractCall(ADD_BIFI_TOKEN, SIG_ALLOWANCE, [walletAddress, ADD_GBIFI_TOKEN], ['uint256'])
        .then(response => setWalletAllowance(Wallet.bn2Str(new BN(response).div(DEFAULT_DECIMALS))))
        .catch(() => setWalletAllowance('0'))
        .finally(() => setInitialized(true));
    };

    const intervalID = setInterval(syncData, BLOCK_TERM);

    return () => clearInterval(intervalID);
  }, [wallet, walletAddress, chainID]);

  // media query
  const isMobile = useMediaQuery({query: '(max-width:767px)'});

  // event function
  const connectWalletClick: React.MouseEventHandler<HTMLElement> = (): void => {
    wallet
      .connectWallet()
      .catch(error => console.error('connect wallet', error));
  };
  const inputAmountChange: React.ChangeEventHandler<HTMLInputElement> = (e): void => {
    const numberValue = parseFloat(e.target.value);
    const numberBifiBalance = parseFloat(selectedTokenBalance || '0');

    if (!isNaN(numberValue)) {
      setInputAmount(`${Math.min(numberBifiBalance, numberValue)}`);
    } else {
      setInputAmount('');
    }
  };
  const maxClick: React.MouseEventHandler<HTMLElement> = (): void => setInputAmount(selectedTokenBalance);
  const changeTokenClick: React.MouseEventHandler<HTMLElement> = (): void => setSelectedTokenID((tokens.find(token => token.id !== selectedTokenID) || {}).id || '');
  const approveClick: React.MouseEventHandler<HTMLElement> = (): void => {
    const amount = Wallet.bn2Hex(new BN(MAX_VALUE_UINT256));
    const args: any[] = [ADD_GBIFI_TOKEN, amount];

    wallet
    .sendWithSignature(ADD_BIFI_TOKEN, SIG_APPROVE, args)
    .then(() => setInputAmount(''))
    .catch(error => console.error(SIG_APPROVE, error));
  };
  const swapClick: React.MouseEventHandler<HTMLElement> = (): void => {
    if (selectedTokenID === 'gbifi') {
      withdrawTo();
    } else {
      depositFor();
    }
  };
  const languageChange: React.ChangeEventHandler<HTMLSelectElement> = (e): void => {
    const newValue = e.target.value;

    localStorage.setItem('language', newValue);

    i18n.changeLanguage(newValue);
  };
  const registerTokenClick: React.MouseEventHandler<HTMLElement> = (): void => {
    setPopup(true);
  };
  const popupClose: React.MouseEventHandler<HTMLElement> = (): void => {
    setPopup(false);
  };

  // function
  const depositFor = (): void => {
    const account = walletAddress;
    const amount = Wallet.bn2Hex(new BN(inputAmount).multipliedBy(DEFAULT_DECIMALS));
    const args: any[] = [account, amount];

    if (args.includes('')) {
      alert('Please enter a valid amount');
    }

    wallet
      .sendWithSignature(ADD_GBIFI_TOKEN, SIG_DEPOSIT_FOR, args)
      .then(() => setInputAmount(''))
      .catch(error => console.error(SIG_DEPOSIT_FOR, error));
  };
  const withdrawTo = (): void => {
    const account = walletAddress;
    const amount = Wallet.bn2Hex(new BN(inputAmount).multipliedBy(DEFAULT_DECIMALS));
    const args: any[] = [account, amount];

    if (args.includes('')) {
      alert('Please enter a valid amount');
    }

    wallet
      .sendWithSignature(ADD_GBIFI_TOKEN, SIG_WITHDRAW_TO, args)
      .then(() => setInputAmount(''))
      .catch(error => console.error(SIG_WITHDRAW_TO, error));
  };

  // template
  return (
    <div className={`App ${selectedTokenID}`}>
      <header className="App-Header">
        <img src={logo} className="App-Header__Left" alt="governance" />
        <div className="App-Header__Right">
          <div
            className="App-Header-Dot"
            style={{ backgroundColor: Wallet.getChainColor(chainID) }}
          />
          <div className="App-Header-Network">{Wallet.getChainName(chainID)}</div>
          <button className="App-Header-Wallet material-button" onClick={connectWalletClick}>
            {middleEllipsis(walletAddress) || t('connectWallet')}
          </button>
        </div>
      </header>
      <div className="App-logo-Container">
        <div className="App-logo-Container-Background" />
        <img className="App-logo" src={main} alt="main" />
      </div>
      <div className="App-Payload">{t('title')}</div>
      <div className="App-Form">
        {errorOccured && <div className="App-Form-Error">{t('form.error.allowance')}</div>}
        {initializing && (
          <div className="App-Form-Initializing">
            <span className="App-Form-Initializing-Title">{t('form.initializing')}</span>
            <Loading color="#008080" />
          </div>
        )}
        <div className="App-Form-LabelContainer">
          <div className="App-Form-Label">
            <img className="App-Form-Label-icon" src={selectedTokenImage} alt="icon" />
            {t('form.amount.title', { symbol: selectedTokenSymbol })}
          </div>
          <div className="App-Form-Label">
            {t('form.amount.balance')} : {selectedTokenBalance} {selectedTokenSymbol}
          </div>
        </div>
        <div
          className="input-container App-Form-AmountContainer"
          style={enabledStyleProvider(inputAmountEnabled)}>
          <input
            className="App-Form-AmountContainer-Input"
            type="number"
            placeholder={isMobile ? '' : t('form.amount.placeholder', { amount: '0', symbol: selectedTokenSymbol })}
            value={inputAmount}
            onChange={inputAmountChange}
          />
          <div className="App-Form-Input__MAX material-button flat" onClick={maxClick}>
            MAX
          </div>
        </div>
        <div className="App-Form-ChangeContainer">
          <div className="App-Form-Change material-button" onClick={changeTokenClick}>
            <ArrowDownward className="App-Form-ChangeIcon" />
          </div>
        </div>
        <div className="App-Form-LabelContainer">
          <div className="App-Form-Label">
            <img className="App-Form-Label-icon" src={unselectedTokenImage} alt="icon" />
            {t('form.amount.title', { symbol: unselectedTokenSymbol })}
          </div>
        </div>
        <div
          className="input-container App-Form-AmountContainer"
          style={{"pointerEvents": "none", "opacity": "0.4"}}>
          <input
            className="App-Form-AmountContainer-Input"
            readOnly
            value={inputAmount}
          />
        </div>
        {buttonApproveTokenRequired && (
          <button className="App-Form-Submit material-button" onClick={approveClick}>
            {t('form.approve')}
          </button>
        )}
        <button
          className="App-Form-Submit material-button"
          onClick={swapClick}
          style={enabledStyleProvider(buttonSwapEnabled)}>
          {t('form.swap')}
        </button>
      </div>
      <div className="App-Footer-Wrapper">
        <button
          className="App-Footer-Button material-button"
          onClick={registerTokenClick}
          style={enabledStyleProvider(!!chainID)}>
          {t('form.registerToken')}
        </button>
        <select className="App-Language" onChange={languageChange} value={i18n.language}>
          <option value="en">English</option>
          <option value="ko">한국어</option>
          <option value="zh">简体中文</option>
        </select>
      </div>
      <PopupRegisterToken wallet={wallet} open={popup} handleClose={popupClose} />
    </div>
  );
}

export default App;
